import React, { useState, useEffect } from "react";
import InputText from "../InputFields/InputText";
import InputDropdown from "../InputFields/InputDropdown";
import GooglePlacesAutocomplete from "../GooglePlacesAutocomplete/GooglePlacesAutocomplete";
import { Col, Row } from "reactstrap";
import "./style.css";
import { Grid } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import CloseIcon from "@mui/icons-material/Close";
import { Country } from "country-state-city";

// US States data from CSV
const US_STATES = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "District of Columbia", code: "DC" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
];

const Address = ({
  handleChange,
  formik,
  onAddressChange,
  onCountryChange,
  onAddressDataForPayment,
  submitAttempted,
  /** When true, address fields are optional (no * / no required-empty errors). */
  addressFieldsOptional = false,
  sx: rootSx,
}) => {
  const requireAddress = !addressFieldsOptional;
  const [isCustomAddress, setIsCustomAddress] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    setCountries(Country.getAllCountries() || []);
  }, []);

  useEffect(() => {
    if (
      !formik?.values?.Country ||
      String(formik.values.Country).trim() === ""
    ) {
      setSelectedCountry(null);
      if (onCountryChange) onCountryChange(null);
      return;
    }
    if (countries?.length > 0) {
      const countryObj = countries.find(
        (c) => c?.name?.toLowerCase() === formik.values.Country?.toLowerCase(),
      );
      const next = countryObj || null;
      setSelectedCountry(next);
      if (onCountryChange) onCountryChange(next);
    }
  }, [formik?.values?.Country, countries]);

  // Notify parent with address payload for payment forms (address1, city, state, zip, countryCode)
  useEffect(() => {
    if (!onAddressDataForPayment || !formik?.values) return;
    const { Address: addr1, City: city, State: state, Zip: zip, Country: countryName } = formik.values;
    const countryObj = countries?.find(
      (c) => c?.name?.toLowerCase() === countryName?.toLowerCase() || c?.isoCode === countryName
    );
    const countryCode = countryObj?.isoCode || (countryName?.length === 2 ? countryName : "") || "";
    onAddressDataForPayment({
      address1: addr1 ?? "",
      city: city ?? "",
      state: state ?? "",
      zip: zip ?? "",
      countryCode: countryCode || "",
    });
  }, [
    onAddressDataForPayment,
    formik?.values?.Address,
    formik?.values?.City,
    formik?.values?.State,
    formik?.values?.Zip,
    formik?.values?.Country,
    countries,
  ]);

  const handleGooglePlaceSelect = (placeData) => {
    if (!placeData) {
      return;
    }

    // Check if this is the raw Google Places data (has address_components)
    if (placeData?.address_components) {
      return;
    }

    const {
      street = "",
      city = "",
      state = "",
      zip = "",
      country = "",
      formatted_address = "",
    } = placeData;

    // Use the parsed street address from Google Places, or fallback to first part of formatted address
    const searchAddress = street || formatted_address?.split(",")[0]?.trim();

    // Set form values
    formik.setValues({
      ...formik.values,
      Address: searchAddress,
      City: city || "",
      State: state || "",
      Zip: zip || "",
      Country: country || "",
    });

    // Set selected state if state is provided
    if (state) {
      const stateObj = US_STATES.find(
        (s) => s.name === state || s.code === state,
      );
      if (stateObj) {
        setSelectedState(stateObj);
        // Update formik value to use state code instead of name
        formik.setFieldValue("State", stateObj.code);
      }
    }

    // Clear errors immediately
    const newErrors = { ...formik.errors };
    if (city) delete newErrors?.City;
    if (state) delete newErrors?.State;
    if (zip) delete newErrors?.Zip;
    if (country) delete newErrors?.Country;
    if (searchAddress) delete newErrors?.Address;
    formik.setErrors(newErrors);

    // Set touched fields and validate
    setTimeout(() => {
      formik.setTouched({
        ...formik.touched,
        Address: true,
        City: true,
        State: true,
        Zip: true,
        Country: true,
      });

      formik.validateForm().then(() => {});
    }, 100);

    if (country) {
      const countryObj = countries.find(
        (c) => c?.name?.toLowerCase() === country?.toLowerCase(),
      );
      if (countryObj) {
        setSelectedCountry(countryObj);
      }
    }

    if (onAddressChange) onAddressChange();
  };

  const handleCustomAddressToggle = () => {
    setIsCustomAddress(true);
    formik.setValues({
      ...formik.values,
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Country: "",
    });
    setSelectedState(null);
    if (onAddressChange) onAddressChange();
  };

  const handleCloseCustomAddress = () => {
    setIsCustomAddress(false);
    formik.setValues({
      ...formik.values,
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Country: "",
    });
    setSelectedState(null);
    if (onAddressChange) onAddressChange();
  };

  // Sync selectedState with formik values
  useEffect(() => {
    if (formik?.values?.State && !selectedState) {
      const stateObj = US_STATES.find(
        (s) => s.code === formik.values.State || s.name === formik.values.State,
      );
      if (stateObj) {
        setSelectedState(stateObj);
      }
    }
  }, [formik?.values?.State, selectedState]);

  useEffect(() => {
    const newErrors = { ...formik?.errors };
    let hasChanges = false;

    if (formik?.values?.City && newErrors?.City) {
      delete newErrors?.City;
      hasChanges = true;
    }
    if (formik?.values?.State && newErrors?.State) {
      delete newErrors?.State;
      hasChanges = true;
    }
    if (formik?.values?.Zip && newErrors?.Zip) {
      delete newErrors?.Zip;
      hasChanges = true;
    }
    if (formik?.values?.Country && newErrors?.Country) {
      delete newErrors?.Country;
      hasChanges = true;
    }
    if (formik?.values?.Address && newErrors?.Address) {
      delete newErrors?.Address;
      hasChanges = true;
    }

    if (hasChanges) {
      formik.setErrors(newErrors);
    }
  }, [
    formik?.values?.City,
    formik.values.State,
    formik?.values?.Zip,
    formik?.values?.Country,
    formik?.values?.Address,
  ]);

  const sortedCountries = React.useMemo(() => {
    if (!countries || countries?.length === 0) return [];
    const us = countries?.find(
      (c) => c?.name?.toLowerCase() === "united states",
    );
    const rest = countries?.filter(
      (c) => c?.name?.toLowerCase() !== "united states",
    );
    return us ? [us, ...rest] : countries;
  }, [countries]);

  return (
    <Grid
      className="d-flex flex-column cs-address-form-root"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "visible",
        pt: 0,
        gap: 2,
        ...rootSx,
      }}
    >
      {isCustomAddress ? (
        <InputText
          value={formik?.values?.Address}
          onChange={(e) => {
            handleChange(e);
            if (
              e.target.value &&
              e.target.value.trim() !== "" &&
              formik?.errors?.Address
            ) {
              formik.setFieldError("Address", undefined);
            }
          }}
          onBlur={(e) => {
            formik?.handleBlur(e);
            formik.setFieldTouched("Address", true);
          }}
          error={Boolean(
            (requireAddress &&
              (submitAttempted || formik?.touched?.Address) &&
              (!formik?.values?.Address ||
                String(formik?.values?.Address).trim() === "")) ||
              (formik?.errors?.Address &&
                (submitAttempted || formik?.touched?.Address)),
          )}
          helperText={undefined}
          showErrorOnly={true}
          type="text"
          name="Address"
          label="Enter Full Address"
          required={requireAddress}
          placeholder="Enter full address"
          multiline
          rows={3}
          className="text-blue-color w-100 border-blue-color userInfoAddress"
          endAdornment={
            <button
              type="button"
              onClick={handleCloseCustomAddress}
              className="cs-address-close-btn"
              aria-label="Back to address search"
            >
              <CloseIcon sx={{ fontSize: 18, color: "#475569" }} />
            </button>
          }
        />
      ) : (
        <GooglePlacesAutocomplete
          label="Street Address"
          required={requireAddress}
          fieldHeight="44px"
          placeholder="Start typing to search for an address..."
          value={formik?.values?.Address}
          onChange={handleGooglePlaceSelect}
          onBlur={() => formik.setFieldTouched("Address", true)}
          error={Boolean(
            (requireAddress &&
              (submitAttempted || formik?.touched?.Address) &&
              (!formik?.values?.Address ||
                String(formik?.values?.Address).trim() === "")) ||
              (formik?.errors?.Address &&
                (submitAttempted || formik?.touched?.Address)),
          )}
          helperText={undefined}
          fullWidth={true}
          className="text-blue-color w-100 border-blue-color userInfoAddress"
          disableClearable={true}
          extraEndAdornment={
            <button
              type="button"
              onClick={handleCustomAddressToggle}
              className="cs-address-custom-btn"
              aria-label="Enter address manually"
            >
              <HomeIcon sx={{ fontSize: 18, color: "#475569" }} />
              <span>Custom</span>
            </button>
          }
        />
      )}
      <Row
        className="m-0 p-0 address-field-pair-row align-items-start"
        style={{ overflow: "visible", paddingTop: "4px" }}
      >
        <Col
          lg={6}
          md={12}
          sm={12}
          xs={12}
          className="m-0 p-0 mt-0 pt-0"
          style={{ overflow: "visible" }}
        >
          <InputText
            value={formik?.values?.City}
            onChange={(e) => {
              const value = e?.target?.value;
              if (/^[A-Za-z\s]*$/.test(value)) {
                handleChange(e);
                if (value && value.trim() !== "" && formik?.errors?.City) {
                  formik.setFieldError("City", undefined);
                }
              }
            }}
            onBlur={(e) => {
              formik?.handleBlur(e);
              formik.setFieldTouched("City", true);
            }}
            error={Boolean(
              (requireAddress &&
                (submitAttempted || formik?.touched?.City) &&
                (!formik?.values?.City ||
                  String(formik?.values?.City).trim() === "")) ||
              (formik?.errors?.City &&
                (submitAttempted || formik?.touched?.City)),
            )}
            helperText={undefined}
            showErrorOnly={true}
            name="City"
            label="City"
            required={requireAddress}
            type="text"
            placeholder="Enter city"
            className="text-blue-color w-100"
            fieldHeight="44px"
          />
        </Col>
        <Col
          lg={6}
          md={12}
          sm={12}
          xs={12}
          className="m-0 p-0 mt-3 pt-0 ps-lg-2"
          style={{ overflow: "visible" }}
        >
            <InputDropdown
              onChange={(event, newValue) => {
                setSelectedState(newValue);
                const stateCode = newValue?.code || "";
                formik.setFieldValue("State", stateCode);

                if (stateCode) {
                  formik.setFieldError("State", undefined);
                } else if (requireAddress && submitAttempted && formik?.errors?.State) {
                  formik.setFieldError("State", undefined);
                }

                formik.setFieldTouched("State", true, false);

                if (handleChange) {
                  handleChange({
                    target: {
                      name: "State",
                      value: stateCode,
                    },
                  });
                }
              }}
              options={US_STATES}
              value={selectedState || null}
              onTextFieldChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setSelectedState(null);
                  formik.setFieldValue("State", "");
                } else {
                  // Find state by name or code
                  const stateObj = US_STATES.find(
                    (s) =>
                      s.name.toLowerCase() === value.toLowerCase() ||
                      s.code.toLowerCase() === value.toLowerCase(),
                  );
                  if (stateObj) {
                    setSelectedState(stateObj);
                    formik.setFieldValue("State", stateObj.code);
                  }
                }
              }}
              onBlur={(e) => {
                formik?.handleBlur(e);
                formik.setFieldTouched("State", true);
              }}
              textFieldProps={{
                ...formik?.getFieldProps("State"),
                id: "address-state-select",
              }}
              getOptionLabel={(option) => option?.name || ""}
              error={Boolean(
                (requireAddress &&
                  (submitAttempted || formik?.touched?.State) &&
                  (!selectedState ||
                    !formik?.values?.State ||
                    String(formik?.values?.State).trim() === "")) ||
                (formik?.errors?.State &&
                  (submitAttempted || formik?.touched?.State)),
              )}
              helperText={undefined}
              showErrorOnly={true}
              filterOptions={(options, state) =>
                options?.filter(
                  (option) =>
                    option?.name
                      ?.toLowerCase()
                      .includes(state?.inputValue?.toLowerCase()) ||
                    option?.code
                      ?.toLowerCase()
                      .includes(state?.inputValue?.toLowerCase()),
                )
              }
              name="State"
              label="State"
              required={requireAddress}
              type="text"
              placeholder="Enter state"
              fieldHeight="44px"
            />
        </Col>
      </Row>
      <Row
        className="m-0 p-0 address-field-pair-row align-items-start"
        style={{ overflow: "visible", paddingTop: "2px" }}
      >
        <Col
          lg={6}
          md={12}
          sm={12}
          xs={12}
          className="m-0 p-0 mt-0 pt-0"
          style={{ overflow: "visible" }}
        >
          <InputText
            value={formik?.values?.Zip}
            onChange={(e) => {
              const value = e?.target?.value;
              if (/^\d{0,5}(-\d{0,4})?$/.test(value)) {
                handleChange(e);
                if (value && value.trim() !== "" && formik?.errors?.Zip) {
                  formik.setFieldError("Zip", undefined);
                }
              }
            }}
            onBlur={(e) => {
              formik?.handleBlur(e);
              formik.setFieldTouched("Zip", true);
            }}
            error={Boolean(
              (requireAddress &&
                (submitAttempted || formik?.touched?.Zip) &&
                (!formik?.values?.Zip ||
                  String(formik?.values?.Zip).trim() === "")) ||
              (formik?.errors?.Zip &&
                (submitAttempted || formik?.touched?.Zip)),
            )}
            helperText={undefined}
            showErrorOnly={true}
            name="Zip"
            label="ZIP Code"
            required={requireAddress}
            type="text"
            className="text-blue-color w-100 m-0"
            fieldHeight="44px"
            maxLength="10"
            placeholder="Enter zip code"
          />
        </Col>
        <Col
          lg={6}
          md={12}
          sm={12}
          xs={12}
          className="m-0 p-0 mt-0 pt-0 ps-lg-2"
          style={{ overflow: "visible" }}
        >
            <InputDropdown
              onChange={(event, newValue) => {
                setSelectedCountry(newValue);
                const countryName = newValue?.name || "";
                formik.setFieldValue("Country", countryName);

                if (countryName) {
                  formik.setFieldError("Country", undefined);
                } else if (requireAddress && submitAttempted && formik?.errors?.Country) {
                  formik.setFieldError("Country", undefined);
                }
                formik.setFieldTouched("Country", true, false);
                if (onCountryChange) onCountryChange(newValue);

                if (handleChange) {
                  handleChange({
                    target: {
                      name: "Country",
                      value: countryName,
                    },
                  });
                }
              }}
              options={sortedCountries}
              value={selectedCountry || null}
              onTextFieldChange={(e) => {
                formik?.handleChange(e);
                const value = e.target.value;
                if (!value) {
                  setSelectedCountry(null);
                }
              }}
              onBlur={(e) => {
                formik?.handleBlur(e);
                formik.setFieldTouched("Country", true);
              }}
              textFieldProps={{
                ...formik?.getFieldProps("Country"),
                id: "address-country-select",
              }}
              getOptionLabel={(option) => option?.name || ""}
              error={Boolean(
                (requireAddress &&
                  (submitAttempted || formik?.touched?.Country) &&
                  (!selectedCountry ||
                    !formik?.values?.Country ||
                    String(formik?.values?.Country).trim() === "")) ||
                (formik?.errors?.Country &&
                  (submitAttempted || formik?.touched?.Country)),
              )}
              helperText={undefined}
              showErrorOnly={true}
              filterOptions={(options, state) =>
                options?.filter((option) =>
                  option?.name
                    ?.toLowerCase()
                    .includes(state?.inputValue?.toLowerCase()),
                )
              }
              name="Country"
              label="Country"
              required={requireAddress}
              type="text"
              placeholder="Enter country"
              fieldHeight="44px"
            />
        </Col>
      </Row>
    </Grid>
  );
};

/** Resolve country code or name to full name. Use when setting form values from API (e.g. profile.country "US" → "United States"). */
export function getCountryNameFromCodeOrName(codeOrName) {
  if (!codeOrName) return "United States";
  const list = Country?.getAllCountries() || [];
  const c = list.find((x) => x.isoCode === codeOrName || x.name === codeOrName);
  return c?.name || codeOrName || "United States";
}

export default Address;
